import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { StarIcon, ShieldCheckIcon } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
export function TestimonialsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="w-full bg-dark-section py-20 md:py-28" data-id="element-257">
      <div className="max-w-4xl mx-auto px-6" data-id="element-258">
        <motion.h2 className="font-bebas text-4xl md:text-5xl lg:text-6xl text-white text-center mb-4 tracking-wide" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6
      }} data-id="element-259">
          WHAT <span className="text-orange-accent" data-id="element-260">OTHERS</span> ARE SAYING
        </motion.h2>

        <motion.p className="font-inter text-concrete text-center mb-12" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} data-id="element-261">
          <ShieldCheckIcon className="w-4 h-4 inline mr-2 text-orange-accent" data-id="element-262" />
          Names changed to protect privacy — as it should be
        </motion.p>

        <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6" variants={containerVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-263">
          {/* Testimonial 1 */}
          <motion.div variants={cardVariants} className="bg-dark-card border border-white/5 p-6" data-id="element-264">
            <div className="flex gap-1 mb-4" data-id="element-265">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-orange-accent text-orange-accent" data-id="element-266" />)}
            </div>
            <p className="font-inter text-concrete leading-relaxed mb-4" data-id="element-267">
              "Built mine in the garage floor over a long weekend. My wife
              didn't even know it was there until I showed her.{' '}
              <span className="text-white" data-id="element-268">That's exactly the point.</span> The
              neighbor's house got hit last month — they took his Liberty safe
              with a dolly. Mine? Invisible."
            </p>
            <div className="flex items-center gap-3" data-id="element-269">
              <div className="w-10 h-10 bg-orange-accent/20 rounded-full flex items-center justify-center" data-id="element-270">
                <span className="font-bebas text-orange-accent" data-id="element-271">M.R.</span>
              </div>
              <div data-id="element-272">
                <div className="font-inter text-white text-sm font-semibold" data-id="element-273">
                  Mike R.
                </div>
                <div className="font-inter text-concrete/60 text-xs" data-id="element-274">
                  Texas • Verified Buyer
                </div>
              </div>
            </div>
          </motion.div>

          {/* Testimonial 2 */}
          <motion.div variants={cardVariants} className="bg-dark-card border border-white/5 p-6" data-id="element-275">
            <div className="flex gap-1 mb-4" data-id="element-276">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-orange-accent text-orange-accent" data-id="element-277" />)}
            </div>
            <p className="font-inter text-concrete leading-relaxed mb-4" data-id="element-278">
              "I was skeptical about the $500 claim. Ended up spending $487 at
              Home Depot. The guide is incredibly detailed —{' '}
              <span className="text-white" data-id="element-279">
                every SKU, every measurement, every step.
              </span>{' '}
              No guesswork. Just follow the blueprint."
            </p>
            <div className="flex items-center gap-3" data-id="element-280">
              <div className="w-10 h-10 bg-orange-accent/20 rounded-full flex items-center justify-center" data-id="element-281">
                <span className="font-bebas text-orange-accent" data-id="element-282">J.T.</span>
              </div>
              <div data-id="element-283">
                <div className="font-inter text-white text-sm font-semibold" data-id="element-284">
                  James T.
                </div>
                <div className="font-inter text-concrete/60 text-xs" data-id="element-285">
                  Arizona • Verified Buyer
                </div>
              </div>
            </div>
          </motion.div>

          {/* Testimonial 3 */}
          <motion.div variants={cardVariants} className="bg-dark-card border border-white/5 p-6" data-id="element-286">
            <div className="flex gap-1 mb-4" data-id="element-287">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-orange-accent text-orange-accent" data-id="element-288" />)}
            </div>
            <p className="font-inter text-concrete leading-relaxed mb-4" data-id="element-289">
              "Former LEO here. I've seen what happens when criminals know where
              the safe is.{' '}
              <span className="text-white" data-id="element-290">
                They come back with tools and time.
              </span>{' '}
              This method eliminates that threat entirely. Should be standard
              practice for every gun owner."
            </p>
            <div className="flex items-center gap-3" data-id="element-291">
              <div className="w-10 h-10 bg-orange-accent/20 rounded-full flex items-center justify-center" data-id="element-292">
                <span className="font-bebas text-orange-accent" data-id="element-293">D.K.</span>
              </div>
              <div data-id="element-294">
                <div className="font-inter text-white text-sm font-semibold" data-id="element-295">
                  David K.
                </div>
                <div className="font-inter text-concrete/60 text-xs" data-id="element-296">
                  Florida • Verified Buyer
                </div>
              </div>
            </div>
          </motion.div>

          {/* Testimonial 4 */}
          <motion.div variants={cardVariants} className="bg-dark-card border border-white/5 p-6" data-id="element-297">
            <div className="flex gap-1 mb-4" data-id="element-298">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-orange-accent text-orange-accent" data-id="element-299" />)}
            </div>
            <p className="font-inter text-concrete leading-relaxed mb-4" data-id="element-300">
              "The decoy safe bonus alone was worth the price.{' '}
              <span className="text-white" data-id="element-301">
                I put a cheap Stack-On in the closet with some old watches.
              </span>{' '}
              Real collection is under the workshop floor. Peace of mind I
              haven't had in years."
            </p>
            <div className="flex items-center gap-3" data-id="element-302">
              <div className="w-10 h-10 bg-orange-accent/20 rounded-full flex items-center justify-center" data-id="element-303">
                <span className="font-bebas text-orange-accent" data-id="element-304">R.S.</span>
              </div>
              <div data-id="element-305">
                <div className="font-inter text-white text-sm font-semibold" data-id="element-306">
                  Robert S.
                </div>
                <div className="font-inter text-concrete/60 text-xs" data-id="element-307">
                  Montana • Verified Buyer
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}